/**
 * @name HelpDocDetail
 * @desc Renders the help doc detail for the selected article
 */
import React from 'react';
import axios from './../../axios';
import clsx from 'clsx';
import { MaterialUiCore } from '@gscope-mfe/common-libs';
import { MaterialUiIcons } from '@gscope-mfe/common-libs';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { AppUtils, Utilities } from '@gscope-mfe/app-bridge';
import Loader from '../Loader';
import ZeroSearchHandler from '../ZeroSearchHandler';
import DrawerComponent from '../DrawerComponent';
import parse from 'html-react-parser';
import styles from './HelpDocDetail.module.css';
import 'react-quill/dist/quill.snow.css'; //Custom hook to get Query Param

const { Typography } = MaterialUiCore;
const { CardContent } = MaterialUiCore;
const { CardActions } = MaterialUiCore;
const { Card } = MaterialUiCore;
const { InputAdornment } = MaterialUiCore;
const SearchIcon = MaterialUiIcons.Search;
const { OutlinedInput } = MaterialUiCore;
const { InputLabel } = MaterialUiCore;
const { FormControl } = MaterialUiCore;
const { Link } = MaterialUiCore;
const { Grid } = MaterialUiCore;

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const getHelpDocDetail = async (id, setArticleDetail, setError, setloading) => {
    setloading(true);
    const { data } = await axios.get(`/api/gateway/cms/getHelpDocDetail?articleId=${id}`);

    if (data.statusCode === 200) {
        setArticleDetail(data.payload);
    } else {
        setError(true);
    }

    setloading(false);
};
/**
 * @name getFAQList
 * @desc Fetches the list of articles for a page
 * @param {*} setFAQList
 * @param {*} market
 * @param {*} setloading
 */

const getFAQList = async (setFAQList, market, pageRoute, setloading, setFilteredList) => {
    setloading(true);
    const { data } = await axios.get(`/api/gateway/cms/getPublishedHelpDocs?pageRoute=${pageRoute}&market=${market}`);

    if (data.statusCode === 200 && data.payload.length > 0) {
        setFAQList(data.payload);
        setFilteredList(data.payload);
    }

    setloading(false);
};

const HelpDocDetail = () => {
    const queryParam = useQuery();
    const articleId = queryParam.get('id');
    const pageRoute = queryParam.get('page');
    const [hasPermission, setHasPermission] = React.useState(false);
    const [articleDetail, setArticleDetail] = React.useState(null);
    const [error, setError] = React.useState(false);
    const [faqList, setFAQList] = React.useState([]);
    const [filteredList, setFilteredList] = React.useState([]);
    const [showSidePanel, setShowSidePanel] = React.useState(false);
    const { userInfo, loading, setloading, currentMarket, setHeaderConfig, headerConfig, setBreadCrumbArr } =
        AppUtils.get();

    const renderFAQList = () => (
        <Grid item container justify="flex-start">
            {filteredList.map((item) => (
                <Grid lg={4} md={4} sm={6} xs={12} key={item.id}>
                    <Card variant="outlined" className={styles.articleListItem}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                {item.title}
                            </Typography>
                            <Typography variant="body2" component="span">
                                Last Updated: {moment(item.updated_ts).format('lll')}
                            </Typography>
                        </CardContent>
                        <CardActions className={styles.articleAction}>
                            <Link href={`/helpDoc?page=${pageRoute}&id=${item.id}`} target="_blank">
                                {' '}
                                Learn More
                            </Link>
                        </CardActions>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const getPermission = () => {
        const data = articleId ? atob(articleId).split('_') : [];
        const articlePageRoute = data[1] || null;

        if (articleId && articlePageRoute !== pageRoute) {
            setHasPermission(false);
            return false;
        }
        let permittedRoutes = JSON.parse(localStorage.getItem('ngStorage-routePermission')) || [];
        const mfePermittedRoutes = Utilities.getAllPermittedMfeRoutesInMarket(currentMarket);
        permittedRoutes = [...permittedRoutes, ...mfePermittedRoutes];
        if (permittedRoutes.includes(pageRoute)) {
            setHasPermission(true);
            return true;
        } else {
            setHasPermission(false);
            return false;
        }
    };
    /**
     * @name fetchFilteredDocsList
     * @desc Filters the help doc list based on the searchedVal
     * @param {String} searchedVal
     */

    const fetchFilteredDocsList = (val) => {
        const searchedVal = val.trim();

        if (searchedVal === '' || searchedVal === null) {
            setFilteredList(faqList);
        }

        const filteredData = faqList.filter((item) => item.title.toLowerCase().includes(searchedVal.toLowerCase()));
        setFilteredList(filteredData);
    };

    const getFAQData = async () => {
        setloading(true);
        await getFAQList(setFAQList, currentMarket, pageRoute, setloading, setFilteredList);

        if (!articleId && pageRoute) {
            await setHeaderConfig({ ...headerConfig, title: 'Help Docs' });
        } else if (pageRoute && articleId) {
            await getHelpDocDetail(articleId, setArticleDetail, setError, setloading);
            setHeaderConfig({ ...headerConfig, title: 'Help Docs', defaultLink: `/helpDoc?page=${pageRoute}` });
        }

        setloading(false);
    };

    React.useEffect(() => {
        getPermission();
        getFAQData();
        setBreadCrumbArr([]);
    }, []);
    return (
        <div>
            {!hasPermission && !loading && (
                <ZeroSearchHandler
                    handlerType="noResults"
                    header="Unauthorized Access"
                    subHeader="You don't seem to have access to view this resource"
                />
            )}
            {!articleId && pageRoute && hasPermission && (
                <div className={styles.docListContainer}>
                    <Grid lg={4} md={4} sm={6} xs={12} className={styles.searchBar}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel htmlFor="Search">Search</InputLabel>
                            <OutlinedInput
                                id="search"
                                placeholder="Search for a topic"
                                onChange={(e) => fetchFilteredDocsList(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                labelWidth={60}
                            />
                        </FormControl>
                    </Grid>
                    {filteredList.length === 0 && !loading && (
                        <ZeroSearchHandler
                            handlerType="noResults"
                            header="We’re sorry..."
                            subHeader="We can’t seem to find any results that match your search."
                        />
                    )}
                    {
                        // Articles List
                        renderFAQList()
                    }
                </div>
            )}
            {articleDetail && (
                <Grid item container justify="flex-start" className={styles.helpDocGrid}>
                    <Grid lg={3} md={3} className={styles.listContainer}>
                        <div>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel htmlFor="Search">Search</InputLabel>
                                <OutlinedInput
                                    id="search"
                                    placeholder="Search"
                                    onChange={(e) => fetchFilteredDocsList(e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    }
                                    labelWidth={60}
                                />
                            </FormControl>
                            {filteredList.length > 0 ? (
                                <ul className={styles.sidePanelContainer}>
                                    {filteredList.map((item) => (
                                        <li key={item.id}>
                                            <Link href={`/helpDoc?page=${pageRoute}&id=${item.id}`} target="_blank">
                                                {item.title}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className={styles.noDataFound}>No Data Found</p>
                            )}
                        </div>
                    </Grid>
                    <Grid lg={9} md={9}>
                        <div className={styles.helpDocContainer}>
                            <h1 className={styles.articleTitle}>{articleDetail.title}</h1>
                            <div className="ql-editor">{parse(articleDetail.content)}</div>
                        </div>
                    </Grid>
                </Grid>
            )}
            {error && (
                <ZeroSearchHandler
                    handlerType="noResults"
                    header="We’re sorry..."
                    subHeader="We are facing issues while fetching your results. Please try again later"
                />
            )}
        </div>
    );
};

export default HelpDocDetail;
